import React from 'react';
import { dispatch, useSelector } from '@state/store';
import {
  getDealFinderIsLoading,
  getDealFinderResultsData
} from '@state/slices/deal-finder-results/dealFinderResultsSelectors';
import { clearMonthly } from '@state/slices/price-calendar/monthly/priceCalendarMonthlySlice';
import {
  getDealFinderAirports,
  getDealFinderBoardBasis,
  getDealFinderOccupancy,
  getSearchType
} from '@state/slices/deal-finder/dealFinderSelectors';
import { DealFinderTile } from '@components/common/deal-finder-tile/DealFinderTile';
import { Page } from '@model/common';
import { Result, SearchType } from '@model/iceberg/deal-finder/deal-finder';
import { getLoading, performNavigate, RequestType, setPackageReferences } from '@state/slices/app';
import { loadMore } from '@state/slices/deal-finder-results/dealFinderResultsSlice';
import { ResultsGrid } from '@components/search-results/deal-finder-results/results-grid/ResultsGrid';
import { getDealFinderLastSearchType } from '@state/slices/deal-finder-last-searched/dealFinderLastSearchedSelectors';
import { getDealFinderUrl } from '@util/deal-finder';

interface DealFinderResultsContainerProps {
  isBeforeLoad?: boolean;
}

export const DealFinderResultsContainer = ({ isBeforeLoad }: DealFinderResultsContainerProps) => {
  const {
    results,
    searchToken,
    facets: {
      counts: { filtered }
    }
  } = useSelector(getDealFinderResultsData);
  const isLoading = useSelector(getDealFinderIsLoading);
  const isLoadingMoreResults: boolean = useSelector(getLoading(RequestType.LOAD_MORE_RESULTS));
  const isMoreAvailable: boolean = results.length < filtered;
  const airports = useSelector(getDealFinderAirports);
  const occupancy = useSelector(getDealFinderOccupancy);
  const preferredBoardBasis = useSelector(getDealFinderBoardBasis);
  const searchType = useSelector(getSearchType);
  const lastSearchType = useSelector(getDealFinderLastSearchType);
  const handleOnSelectPackage = (payload: Array<string>, path: string) => {
    dispatch(setPackageReferences(payload));
    dispatch(clearMonthly());
    const packageResult = results.find(({ summary }) => summary?.place?.hotel?.path === path);
    if (packageResult) {
      const {
        summary: { startDate, nights }
      } = packageResult;
      const dealFinderUrl = getDealFinderUrl({
        token: searchToken,
        path,
        page: Page.PRODUCT,
        from: airports,
        duration: nights,
        occupancy,
        date: startDate,
        searchType,
        preferredBoardBasis
      });
      dispatch(performNavigate(dealFinderUrl));
    }
  };
  const handleOnLoadMore = () => dispatch(loadMore());
  const renderTile = (result: Result) => (
    <DealFinderTile
      result={result}
      isGhosting={isLoading || isBeforeLoad}
      onClick={(packageReferences: Array<string>) =>
        handleOnSelectPackage(packageReferences, result.summary.place.hotel!.path)
      }
      hotelOnlySearch={lastSearchType === SearchType.HOTEL_ONLY}
    />
  );
  if (!results.length) return null;
  return (
    <ResultsGrid
      packages={results}
      isLoadingMore={isLoadingMoreResults}
      renderTile={renderTile}
      onLoadMore={handleOnLoadMore}
      isMoreAvailable={isMoreAvailable}
    />
  );
};
